import MobileLoadingOverlay from './mobile/components/MobileLoadingOverlay';
import useDeepCompareEffect from 'use-deep-compare-effect';
import withReducer from 'app/store/withReducer';
import LoadingOverlay from 'app/shared-components/util/LoadingOverlay';
import reducer from 'app/pages/store';
import React from 'react';
import { Role } from 'API';
import { selectUserCompanies } from 'app/store/userCompaniesSlice';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'app/store';
import {
  getCardProductApplication,
  getFinancialAccountIdThrottled,
  getIntegration,
  selectReviewOnboardSlice,
} from 'app/pages/store/reviewOnboardSlice';

interface PurchaseCardsWrapperProps {
  isMobileApp?: boolean;
  children: React.ReactNode;
}

const PurchaseCardsWrapper = ({ isMobileApp = false, children }: PurchaseCardsWrapperProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { selectedPaidolId, selectedPaidol, userRole, user } = useAppSelector(selectUserCompanies);
  const { integration, application } = useAppSelector(selectReviewOnboardSlice);

  const userIsAdministrator = Role.ADMINISTRATOR === userRole;

  const ROUTES = {
    ADDITIONAL_DOCUMENTS: '/review/additionalDocuments',
    PERMISSION: '/review/permission',
    ONBOARD: '/review/onboard',
    LANDING: '/landing',
  };

  useDeepCompareEffect(() => {
    if (!integration && selectedPaidolId) {
      dispatch(getIntegration(selectedPaidolId));
    }
  }, [dispatch, integration, selectedPaidolId]);

  useDeepCompareEffect(() => {
    // Check application status - polls until webhook updates the integration
    // The CARD_PRODUCT_APPLICATION_APPROVED webhook now handles setting
    // isApplicationAccepted and financialAccountId automatically
    if (
      integration &&
      integration.accountHolderCardProductApplicationId &&
      !integration.isApplicationAccepted
    ) {
      dispatch(getCardProductApplication(integration));
    }
  }, [
    dispatch,
    integration,
    integration?.accountHolderCardProductApplicationId,
    integration?.isApplicationAccepted,
  ]);

  useDeepCompareEffect(() => {
    // Poll for financial account ID in case frontend has stale data
    // after webhook has processed the application approval
    if (integration && integration?.isApplicationAccepted && !integration.financialAccountId) {
      dispatch(getFinancialAccountIdThrottled(integration));
    }
  }, [dispatch, integration, integration?.isApplicationAccepted, integration?.financialAccountId]);

  /* Vince said comment out payables logic */
  /* if (selectedPaidol && !selectedPaidol.enablePCards) {
    return <Navigate to="/payables" replace />;
  } */

  useDeepCompareEffect(() => {
    // For mobile app, redirect to landing if on any review route
    if (isMobileApp && /^\/review\//.test(location.pathname)) {
      navigate(ROUTES.LANDING, { replace: true });
      return;
    }

    const isStepperComplete = selectedPaidol?.firstRunChecklist?.isStepperComplete === true;
    const isAdditionalDocumentsPage = /^\/review\/additionalDocuments/.test(location.pathname);
    const isPermissionPage = /^\/review\/permission/.test(location.pathname);
    const isOnboardPage = /^\/review\/onboard/.test(location.pathname);
    const isAppAccepted = integration?.isApplicationAccepted === true;

    if (!selectedPaidol || !integration) {
      return;
    }

    if (userIsAdministrator) {
      if (!isAppAccepted && application?.needDocuments === true && !isAdditionalDocumentsPage) {
        navigate(ROUTES.ADDITIONAL_DOCUMENTS, { replace: true });
        return;
      }
      if (!isAppAccepted && !isPermissionPage && application?.needDocuments === false) {
        navigate(ROUTES.PERMISSION, { replace: true });
        return;
      }
      if (isAppAccepted && !isPermissionPage && !integration?.financialAccountId) {
        navigate(ROUTES.PERMISSION, { replace: true });
        return;
      }

      if (
        integration.financialAccountId &&
        !isStepperComplete &&
        isAppAccepted &&
        !isOnboardPage &&
        !user?.is_support
      ) {
        navigate(ROUTES.ONBOARD, { replace: true });
        return;
      }
    }
  }, [
    userIsAdministrator,
    selectedPaidol,
    integration,
    location.pathname,
    integration?.financialAccountId,
    application?.needDocuments,
    integration?.isApplicationAccepted,
    user?.is_support,
    navigate,
    ROUTES.ADDITIONAL_DOCUMENTS,
    ROUTES.PERMISSION,
    ROUTES.ONBOARD,
    ROUTES.LANDING,
    isMobileApp,
  ]);

  // Show loading state for administrators while data loads
  // This prevents flash of home screen before redirect logic runs
  const isAppAccepted = integration?.isApplicationAccepted === true;
  const needsApplicationData = !isAppAccepted && integration && application == null;

  if (userIsAdministrator && selectedPaidolId && (!integration || needsApplicationData)) {
    return isMobileApp ? (
      <MobileLoadingOverlay isLoading={true} />
    ) : (
      <LoadingOverlay isLoading={true} blockInteraction={true} />
    );
  }

  return <>{children}</>;
};

export default withReducer('cards', reducer)(PurchaseCardsWrapper);
